// src/dnd/emitter_tool/hooks/fog.js
import * as PIXI from "pixi.js";
import { BlurFilter } from "pixi.js";

/* ---------- small utils ---------- */
const clamp = (v, a, b) => Math.max(a, Math.min(b, v));
const clamp01 = (v) => clamp(v, 0, 1);
const isDO = (x) => !!x && typeof x.getGlobalPosition === "function";
const TAU = Math.PI * 2;

/* ---------- canvas radial gradient (matches DarknessOverlay stops) ---------- */
function makeWhiteRadialTextureCanvas(size = 512) {
  const cvs = document.createElement("canvas");
  cvs.width = size; cvs.height = size;
  const ctx = cvs.getContext("2d");
  const cx = size / 2, cy = size / 2;
  const r = size / 2;

  const g = ctx.createRadialGradient(cx, cy, 0, cx, cy, r);
  g.addColorStop(0.00, "rgba(255,255,255,1.0)");
  g.addColorStop(0.20, "rgba(255,255,255,1.0)");
  g.addColorStop(0.50, "rgba(255,255,255,0.30)");
  g.addColorStop(1.00, "rgba(255,255,255,0.00)");

  ctx.fillStyle = g;
  ctx.beginPath();
  ctx.arc(cx, cy, r, 0, Math.PI * 2);
  ctx.fill();

  const tex = PIXI.Texture.from(cvs);
  tex.baseTexture.scaleMode = PIXI.SCALE_MODES.LINEAR;
  return tex;
}

/* ---------- LOS helpers (stage space; no map) ---------- */
function buildSegmentsFromPolys(polys) {
  const out = [];
  for (const poly of polys || []) {
    if (!poly || poly.length < 2) continue;
    for (let i = 0; i < poly.length - 1; i++) out.push([poly[i], poly[i + 1]]);
    const first = poly[0], last = poly[poly.length - 1];
    if (first.x !== last.x || first.y !== last.y) out.push([last, first]);
  }
  return out;
}

function computeVisibilityPolygonByIntersect(origin, segments, maxDistance, rays = 192) {
  const pts = [];
  const md = Math.max(2, maxDistance || 1000);

  for (let i = 0; i < rays; i++) {
    const ang = (TAU * i) / rays;
    const dx = Math.cos(ang), dy = Math.sin(ang);
    let tMin = md;
    for (const seg of (segments || [])) {
      const a = seg[0], b = seg[1];
      const v = { x: b.x - a.x, y: b.y - a.y };
      const denom = dx * v.y - dy * v.x;
      if (Math.abs(denom) < 1e-8) continue;
      const apx = a.x - origin.x, apy = a.y - origin.y;
      const t = (apx * v.y - apy * v.x) / denom;
      const u = (apx * dy - apy * dx) / denom;
      if (u < 0 || u > 1) continue;
      if (t >= 0 && t < tMin) tMin = t;
    }
    const tHit = Math.min(tMin, md);
    pts.push({ x: origin.x + dx * tHit, y: origin.y + dy * tHit });
  }
  return pts;
}

/* ---------- Fog composite (solid + ERASE holes; LOS-clipped) ---------- */
/**
 * API:
 * - setFogAlpha(a)
 * - setWalls(polys)
 * - setDarksightGreyscale(enabled, factor01)
 * - setGridScale({ pxPerMeter, metre_in_pixels, meter_in_pixels, square_length })
 * - setFeatherBlur(px)
 * - setGlowEventName(name)
 * - setGlowEnabled(bool)           // Kill switch (OFF by default)
 * - attachToTarget({ ... })
 * - attachDarksightTarget({ ... })
 * - attachGlowPulseAt({ ... })
 * - redraw()
 * - update(dt)
 * - clear() / destroy()
 * - setZIndex(z)
 */
export function buildFogComposite(app, baseAlpha = 1.0, options = {}) {
  const {
    glowEventName: initialGlowEventName = "fx-glow",
    initialFeatherBlur = 8
  } = options || {};

  const fog = new PIXI.Container();
  fog.name = "FogComposite";
  fog.zIndex = -500; // keep this low — your emitter containers use higher zIndex
  // If the parent container has `sortableChildren = true`, zIndex will apply.

  // Soft feathered layer
  const feathered = new PIXI.Container();
  feathered.name = "FogFeatheredLayer";
  fog.addChild(feathered);

  const slab = new PIXI.Graphics();
  slab.name = "FogSlab";
  feathered.addChild(slab);

  const holes = new PIXI.Container();
  holes.name = "FogHoles";
  feathered.addChild(holes);

  // Hard, unblurred ERASE
  const hardLayer = new PIXI.Container();
  hardLayer.name = "FogHardEraseLayer";
  fog.addChild(hardLayer);

  // Darkness-like blur
  const featherFilter = new BlurFilter();
  featherFilter.blur = initialFeatherBlur;
  featherFilter.quality = 1;
  featherFilter.resolution = 1;
  featherFilter.padding = 8;
  feathered.filters = [featherFilter];

  const holeTex = makeWhiteRadialTextureCanvas(512);

  let losSegments = [];

  let fogAlpha = clamp01(baseAlpha);
  let dsGreyscale = true;
  let dsGreyFactor = 0.65;
  const dsErase01 = () => (dsGreyscale ? (1 - clamp01(dsGreyFactor)) : 1);

  let pxPerMeter = 40;
  let squareLengthPx = null;
  function oneSquarePx() {
    if (Number.isFinite(squareLengthPx) && squareLengthPx > 0) return squareLengthPx;
    if (Number.isFinite(pxPerMeter) && pxPerMeter > 0) return pxPerMeter;
    return 50;
  }

  const attached = [];
  const dsAttached = [];
  const pulses = [];

  // --- Glow event plumbing ---
  let _glowEnabled = false;                // OFF by default
  let _eventName = initialGlowEventName;   // which event do we watch
  let _listenerBound = false;

  const onFxGlow = (ev) => {
    if (!_glowEnabled) return; // hard gate
    const d = ev?.detail || {};

    const evPxPerMeter = Number(d.pxPerMeter);
    const ppm = Number.isFinite(evPxPerMeter) && evPxPerMeter > 0 ? evPxPerMeter : pxPerMeter;

    const radiusPx = Number.isFinite(d.radiusPx)
      ? d.radiusPx
      : Math.max(0, Number(d.radiusM) || Number(d.radius_m) || 0) * (ppm || 40);

    const x = Number(d.x);
    const y = Number(d.y);

    if (!Number.isFinite(x) || !Number.isFinite(y) || !(radiusPx > 2)) return;

    attachGlowPulseAt({
      x, y,
      radiusPx,
      alpha: clamp01(d.alpha ?? 1),
      durationS: Math.max(0.02, Number(d.durationS) || (Number(d.durationMs) || 750) / 1000),
      los: d.los !== false,
      fade: d.fade !== false,
    });
  };

  function bindGlowListener() {
    if (_listenerBound) return;
    try { window.addEventListener(_eventName, onFxGlow); _listenerBound = true; } catch {}
  }
  function unbindGlowListener() {
    if (!_listenerBound) return;
    try { window.removeEventListener(_eventName, onFxGlow); } catch {}
    _listenerBound = false;
  }

  function setGlowEnabled(enabled) {
    const next = !!enabled;
    if (next === _glowEnabled) return;
    _glowEnabled = next;

    if (_glowEnabled) {
      bindGlowListener();
    } else {
      // stop listening and purge any active pulses
      unbindGlowListener();
      for (let i = pulses.length - 1; i >= 0; i--) {
        const p = pulses[i];
        try { p.sprite?.destroy(); } catch {}
        try { p.mask?.destroy(true); } catch {}
        try { p.hard?.destroy(true); } catch {}
        try { p.hardMask?.destroy(true); } catch {}
        pulses.splice(i, 1);
      }
    }

    // debug state exposure (optional)
    try {
      window.__FOG_DEBUG = {
        enabled: _glowEnabled,
        listening: _listenerBound,
        eventName: _eventName,
      };
    } catch {}
  }

  function setGlowEventName(name) {
    name = String(name || "").trim() || "fx-glow";
    if (name === _eventName) return;
    const wasEnabled = _glowEnabled;
    if (wasEnabled) unbindGlowListener();
    _eventName = name;
    if (wasEnabled) bindGlowListener();
  }

  function setFogAlpha(a) {
    fogAlpha = clamp01(a);
    redraw();
  }

  function setWalls(polys) {
    losSegments = buildSegmentsFromPolys(polys || []);
  }

  function setDarksightGreyscale(enabled, factor01 = 0.65) {
    dsGreyscale = !!enabled;
    dsGreyFactor = clamp01(factor01);
  }

  function setGridScale(cfg = {}) {
    if (Number.isFinite(cfg.pxPerMeter) && cfg.pxPerMeter > 0) pxPerMeter = cfg.pxPerMeter;
    const mpp = Number.isFinite(cfg.metre_in_pixels) ? cfg.metre_in_pixels : cfg.meter_in_pixels;
    if (Number.isFinite(mpp) && mpp > 0) pxPerMeter = mpp;
    if (Number.isFinite(cfg.square_length) && cfg.square_length > 0) squareLengthPx = cfg.square_length;
  }

  function setFeatherBlur(px) {
    const v = Math.max(0, Number(px) || 0);
    featherFilter.blur = v;
    featherFilter.padding = Math.ceil(v * 2);
  }

  function setZIndex(z) {
    if (Number.isFinite(z)) fog.zIndex = z;
  }

  function redraw() {
    const w = app.renderer.width || 1;
    const h = app.renderer.height || 1;
    slab.clear();
    slab.beginFill(0x000000, fogAlpha);
    slab.drawRect(0, 0, w, h);
    slab.endFill();
  }

  // ---- helpers to construct a LOS mask polygon ----
  function fillMaskPolygon(maskG, center, radiusPx, rays = 192) {
    const w = app.renderer.width || 1;
    const h = app.renderer.height || 1;
    const maxR = Math.max(radiusPx, Math.hypot(w, h));
    const poly = computeVisibilityPolygonByIntersect(center, losSegments, maxR, rays);
    maskG.clear();
    if (poly && poly.length >= 3) {
      maskG.beginFill(0xffffff, 1);
      maskG.moveTo(poly[0].x, poly[0].y);
      for (let j = 1; j < poly.length; j++) maskG.lineTo(poly[j].x, poly[j].y);
      maskG.closePath();
      maskG.endFill();
    }
  }

  // ---- attach regular light-style hole (soft, blurred) ----
  function attachToTarget({ target, radiusPx, durationS = 0, fade = true, los = true, alpha = 1 }) {
    if (!isDO(target)) return null;
    const rPx = Math.max(2, Number(radiusPx) || 0);
    if (rPx <= 2) return null;

    const sp = new PIXI.Sprite(holeTex);
    sp.anchor.set(0.5);
    sp.scale.set((rPx * 2) / holeTex.width);
    sp.alpha = clamp01(alpha);
    sp.blendMode = PIXI.BLEND_MODES.ERASE;
    holes.addChild(sp);

    let maskG = null;
    if (los) {
      maskG = new PIXI.Graphics();
      holes.addChild(maskG);
      sp.mask = maskG;
    }

    const item = {
      sprite: sp,
      mask: maskG,
      target,
      radiusPx: rPx,
      los: !!los,
      life: Math.max(0, durationS),
      t: 0,
      fade: !!fade,
      lastPos: { x: NaN, y: NaN },
      lastT: 0,
    };
    attached.push(item);
    return item;
  }

  // ---- darksight target (soft partial erase + hard inner partial erase, unblurred) ----
  function attachDarksightTarget({ target, radiusPx, los = true }) {
    if (!isDO(target)) return null;
    const rPx = Math.max(8, Number(radiusPx) || 0);
    const erase = clamp01(dsErase01());

    const soft = new PIXI.Sprite(holeTex);
    soft.anchor.set(0.5);
    soft.scale.set((rPx * 2) / holeTex.width);
    soft.alpha = clamp01(0.6 * erase);
    soft.blendMode = PIXI.BLEND_MODES.ERASE;
    holes.addChild(soft);

    let softMask = null;
    if (los) {
      softMask = new PIXI.Graphics();
      holes.addChild(softMask);
      soft.mask = softMask;
    }

    const innerR = Math.max(6, Math.min(rPx * 0.28, oneSquarePx() * 0.6));
    const hard = new PIXI.Graphics();
    hard.blendMode = PIXI.BLEND_MODES.ERASE;
    hard.alpha = erase;
    hardLayer.addChild(hard);

    let hardMask = null;
    if (los) {
      hardMask = new PIXI.Graphics();
      hardLayer.addChild(hardMask);
      hard.mask = hardMask;
    }

    const item = {
      target,
      rPx,
      soft, softMask,
      innerR,
      hard, hardMask,
      los: !!los,
      lastPos: { x: NaN, y: NaN },
      lastT: 0,
    };
    dsAttached.push(item);
    return item;
  }

  // ---- coordinate-based glow pulse (soft with hard core) ----
  function attachGlowPulseAt({ x, y, radiusPx, radiusM, durationS = 0.75, alpha = 1, los = true, fade = true, pxPerMeter: ppmOverride }) {
    if (!_glowEnabled) return null; // hard gate
    const rPx = Number.isFinite(radiusPx) && radiusPx > 0
      ? radiusPx
      : (Number(radiusM) || 0) * (Number(ppmOverride) || pxPerMeter || 40);

    if (!isFinite(x) || !isFinite(y) || !(rPx > 2)) return null;

    const center = { x: Number(x), y: Number(y) };

    const sp = new PIXI.Sprite(holeTex);
    sp.anchor.set(0.5);
    sp.position.set(center.x, center.y);
    sp.scale.set((rPx * 2) / holeTex.width);
    sp.alpha = clamp01(alpha);
    sp.blendMode = PIXI.BLEND_MODES.ERASE;
    holes.addChild(sp);

    let maskG = null;
    if (los) {
      maskG = new PIXI.Graphics();
      holes.addChild(maskG);
      fillMaskPolygon(maskG, center, rPx, 192);
      sp.mask = maskG;
    }

    const innerR = Math.max(6, Math.min(rPx * 0.28, oneSquarePx() * 0.6));
    const hard = new PIXI.Graphics();
    hard.blendMode = PIXI.BLEND_MODES.ERASE;
    hard.alpha = clamp01(alpha);
    hardLayer.addChild(hard);

    let hardMask = null;
    if (los) {
      hardMask = new PIXI.Graphics();
      hardLayer.addChild(hardMask);
      fillMaskPolygon(hardMask, center, innerR, 192);
      hard.mask = hardMask;
    }

    const item = {
      center,
      rPx,
      innerR,
      sprite: sp,
      mask: maskG,
      hard,
      hardMask,
      los: !!los,
      life: Math.max(0, durationS),
      t: 0,
      fade: !!fade,
    };
    pulses.push(item);
    return item;
  }

  function update(dt) {
    // Regular soft attachments
    for (let i = attached.length - 1; i >= 0; i--) {
      const it = attached[i];
      const trg = it.target;
      if (!isDO(trg) || !trg.parent || trg.destroyed) {
        try { it.sprite.destroy(); } catch {}
        try { it.mask?.destroy(true); } catch {}
        attached.splice(i, 1);
        continue;
      }

      const gp = trg.getGlobalPosition();
      it.sprite.position.set(gp.x, gp.y);

      const dx = gp.x - it.lastPos.x;
      const dy = gp.y - it.lastPos.y;
      it.lastT += dt;
      if (!isFinite(dx) || !isFinite(dy) || Math.hypot(dx, dy) > 6 || it.lastT > 0.075) {
        it.lastPos.x = gp.x; it.lastPos.y = gp.y; it.lastT = 0;
        if (it.los && it.mask) {
          fillMaskPolygon(it.mask, gp, it.radiusPx, 192);
        }
      }

      if (it.life > 0) {
        it.t += dt;
        const u = clamp01(it.t / it.life);
        it.sprite.alpha = it.fade ? (1 - u) : it.sprite.alpha;
        if (u >= 1) {
          try { it.sprite.destroy(); } catch {}
          try { it.mask?.destroy(true); } catch {}
          attached.splice(i, 1);
        }
      }
    }

    // Darksight attachments
    const erase = clamp01(dsErase01());
    for (let i = dsAttached.length - 1; i >= 0; i--) {
      const it = dsAttached[i];
      const trg = it.target;
      if (!isDO(trg) || !trg.parent || trg.destroyed) {
        try { it.soft?.destroy(); } catch {}
        try { it.softMask?.destroy(true); } catch {}
        try { it.hard?.destroy(true); } catch {}
        try { it.hardMask?.destroy(true); } catch {}
        dsAttached.splice(i, 1);
        continue;
      }

      const gp = trg.getGlobalPosition();

      if (it.soft) {
        it.soft.position.set(gp.x, gp.y);
        it.soft.alpha = clamp01(0.6 * erase);
      }
      if (it.los && it.softMask) {
        fillMaskPolygon(it.softMask, gp, it.rPx, 192);
      }

      if (it.hard) {
        it.hard.clear();
        it.hard.beginFill(0xffffff, 1);
        it.hard.drawCircle(gp.x, gp.y, it.innerR);
        it.hard.endFill();
        it.hard.alpha = erase;
      }
      if (it.los && it.hardMask) {
        fillMaskPolygon(it.hardMask, gp, it.innerR, 192);
      }
    }

    // Pulses (coordinate-based)
    for (let i = pulses.length - 1; i >= 0; i--) {
      const p = pulses[i];
      if (p.life > 0) {
        p.t += dt;
        const u = clamp01(p.t / p.life);
        const fadeAlpha = p.fade ? (1 - u) : 1;
        if (p.sprite) p.sprite.alpha = fadeAlpha;
        if (p.hard) p.hard.alpha = fadeAlpha;
        if (u >= 1) {
          try { p.sprite?.destroy(); } catch {}
          try { p.mask?.destroy(true); } catch {}
          try { p.hard?.destroy(true); } catch {}
          try { p.hardMask?.destroy(true); } catch {}
          pulses.splice(i, 1);
          continue;
        }
      }

      if (p.los) {
        if (p.mask) fillMaskPolygon(p.mask, p.center, p.rPx, 192);
        if (p.hardMask) fillMaskPolygon(p.hardMask, p.center, p.innerR, 192);
      }
    }
  }

  function clear() {
    try { holes.removeChildren().forEach(c => c.destroy()); } catch {}
    try { hardLayer.removeChildren().forEach(c => c.destroy()); } catch {}
    attached.splice(0, attached.length);
    dsAttached.splice(0, dsAttached.length);
    pulses.splice(0, pulses.length);
  }

  // --- NEW: respond to EditorUIPanel “Hide fog” toggle ---
  let _onHideFog = null;
  try {
    _onHideFog = (e) => {
      const hidden = !!(e?.detail?.hidden);
      // Use visibility so performance is good and layering stays intact
      fog.visible = !hidden;
    };
    window.addEventListener("EmitterTool:UI:HideFog", _onHideFog);
  } catch {}

  function destroy() {
    clear();
    unbindGlowListener();
    try {
      if (_onHideFog) window.removeEventListener("EmitterTool:UI:HideFog", _onHideFog);
    } catch {}
    try { fog.destroy({ children: true }); } catch {}
  }

  redraw();

  // Optional debug helpers: call in DevTools console
  try {
    window.__emit_test_glow = (x, y, radiusPx = 90, durationS = 0.75) => {
      window.dispatchEvent(new CustomEvent(_eventName, {
        detail: { x, y, radiusPx, durationS, alpha: 1, los: true, fade: true }
      }));
    };
    window.__FOG_DEBUG = { enabled: _glowEnabled, listening: _listenerBound, eventName: _eventName };
  } catch {}

  return {
    container: fog,
    setFogAlpha,
    setWalls,
    setDarksightGreyscale,
    setGridScale,
    setFeatherBlur,
    setGlowEventName,
    setGlowEnabled,     // kill switch
    redraw,
    attachToTarget,
    attachDarksightTarget,
    attachGlowPulseAt,
    update,
    clear,
    destroy,
    setZIndex,
  };
}
